/**
 * src/productPages/VirtualShopProductPage.js
 *
 * VISINVOKE Virtual Shop - "CLICK HERE FOR MORE INFO" detail page.
 *
 * Reached from the Virtual Shop website page's shared MoreInfoPill
 * (/dashboard/product/virtual-shop). Built from the standard VISINVOKE
 * marketing primitives so it belongs to the same family as the other product
 * detail pages. Customer-facing only: it explains what the Virtual Shop is, how
 * Shop Credits and acquisition work, what ownership means, and how a collection
 * develops. No backend implementation detail; not physical ecommerce.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ProductNav from '../components/ProductNav';
import { hexA } from '../RegistryComponents';
import {
  VI, MarketingPage, Wrap, Band, SectionHead, Body, Lead,
  PillRow, CardGrid, FlowRail, Callout, NoteStrip, CtaBand,
  BackPill, ActionButton,
} from '../components/marketing/ViMarketing';
import { MoreInfoCinema } from '../components/moreInfo/CyberTechMoreInfoPrimitives';
import '../CyberTechDetailPage.css';

const A = '#7C5CFF';
const BACK = { label: 'VISINVOKE Virtual Shop', route: '/dashboard/virtual-shop' };

const ACQUIRE_STEPS = [
  { n: 'Step 1', title: 'Browse the catalogue', text: 'Explore visual shopping areas, categories and pages. Each page shows a set of product cards so a large catalogue stays easy to move through.' },
  { n: 'Step 2', title: 'Open a product', text: 'View a product’s image, description, Shop Credit price and details, and - where a product is configurable - choose the version you want.' },
  { n: 'Step 3', title: 'Acquire with Shop Credits', text: 'Confirm the item and its Shop Credit price is deducted from your balance. Any configuration you chose is saved with it.' },
  { n: 'Step 4', title: 'It’s yours', text: 'The product is attached to your VISINVOKE account and appears in My Collection, ready to revisit whenever you like.' },
];

const COLLECTION_CARDS = [
  { sub: 'My Collection', title: 'Everything you own, in one place', text: 'Owned products are gathered into My Collection and grouped into meaningful areas, so a growing collection stays organised and easy to browse.' },
  { sub: 'Showcase', title: 'Highlight your favourites', text: 'Feature the pieces you are most proud of in your Showcase - the standouts from everything you have acquired.' },
  { sub: 'Recently Acquired', title: 'See your latest additions', text: 'Recently Acquired keeps your newest items to hand, so it is easy to pick up where you left off.' },
];

function ShopShot({ file, title, caption }) {
  return <figure style={{ margin: '34px 0 0', padding: 0, overflow: 'hidden', borderRadius: 18, border: '1px solid ' + hexA(A, .28), background: '#070b14', boxShadow: '0 28px 72px rgba(0,0,0,.38)' }}>
    <img src={`/product-more-info/virtual-shop/${file}`} alt={title} loading="lazy" style={{ display: 'block', width: '100%', height: 'auto', maxHeight: 760, objectFit: 'contain', objectPosition: 'top center' }} />
    <figcaption style={{ padding: '14px 18px 17px', color: VI.muted, fontSize: 13, lineHeight: 1.55 }}><strong style={{ color: VI.ink, marginRight: 8 }}>{title}</strong>{caption}</figcaption>
  </figure>;
}

export default function VirtualShopProductPage() {
  const navigate = useNavigate();
  const back = <BackPill label={BACK.label} accent={A} onClick={() => navigate(BACK.route)} />;

  return (
    <MarketingPage className="ctmi" background={'radial-gradient(1100px 620px at 50% -22%, ' + hexA(A, 0.26) + ' 0%, transparent 60%), ' + VI.bg}>
      <ProductNav />

      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, transparent, ' + A + ', transparent)' }} />

        {/* ---------------------------------------------------------- HERO */}
        <Band tone="void" pad="tight" style={{
          minHeight: 720,
          display: 'flex',
          alignItems: 'flex-end',
          backgroundImage: 'linear-gradient(90deg, rgba(4,8,20,.97) 0%, rgba(4,8,20,.8) 42%, rgba(4,8,20,.18) 76%), linear-gradient(0deg, #0d1117 0%, transparent 48%), url(/product-more-info/virtual-shop/hero-catalogue.png)',
          backgroundSize: 'cover',
          backgroundPosition: '70% center',
        }}>
          <Wrap>
            <div style={{ padding: '20px 0 6px' }}>{back}</div>
            <div style={{ maxWidth: 980 }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: A, marginBottom: 14 }}>Virtual Shop · More information</div>
              <h1 style={{ fontFamily: VI.font, fontSize: 'clamp(42px, 7vw, 88px)', fontWeight: 900, color: VI.ink, letterSpacing: '-0.065em', lineHeight: 0.94, margin: 0 }}>
                A marketplace of virtual products you own and collect
              </h1>
              <Lead style={{ marginTop: 20 }}>
                The Virtual Shop is a visual marketplace. Explore a large and growing catalogue, acquire the items you like with Shop Credits, own them permanently, and bring them together into a collection that is uniquely yours.
              </Lead>
              <PillRow accent={A} tone="solid" style={{ marginTop: 22 }} items={['Large, growing catalogue', 'Acquire with Shop Credits', 'Persistent ownership', 'Private by default']} />
            </div>
          </Wrap>
        </Band>

        <MoreInfoCinema src="/product-more-info/virtual-shop/marketplace-journey.jpg" alt="Illustrative journey from browsing original virtual objects through acquisition to a private persistent collection." eyebrow="Illustrative marketplace journey" title="Every acquisition begins with a product you can understand." copy="Browse categories, open the product view, review its Shop Credit price and choose any supported configuration before acquiring it." items={[{label:'Browse',value:'Categories & pages'},{label:'Choose',value:'Product & options'},{label:'Acquire',value:'Shop Credits'}]} />

        {/* -------------------------------------------------- WHAT IT IS */}
        <Band tone="panel">
          <Wrap>
            <SectionHead
              align="split"
              accent={A}
              eyebrow="What is Virtual Shop?"
              title="Explore, acquire and build a collection"
              lead="Products are organised into visual shopping areas, categories, catalogues and pages - from everyday collectibles through technology, fashion, equipment, sports, vehicles and property. Open any product to see what it offers and decide whether to acquire it."
            />
            <CardGrid
              accent={A}
              cols={3}
              variant="bar"
              items={[
                { sub: 'Explore', title: 'A catalogue built to browse', text: 'Visual shopping areas and paged product cards keep a large, expanding catalogue easy to explore at your own pace.' },
                { sub: 'Customise', title: 'Configure the version you want', text: 'Some products are configurable - colours, styles, materials, components and upgrades - and the shop keeps your selections valid as you build.' },
                { sub: 'Acquire', title: 'Make it part of your collection', text: 'When you acquire a product it becomes yours, saved with any configuration you chose.' },
              ]}
            />
            <ShopShot file="catalogue-ui.png" title="Real staged catalogue" caption="A populated catalogue shows the breadth, page rhythm and product-card browsing experience." />
          </Wrap>
        </Band>

        {/* --------------------------------------------- SHOP CREDITS */}
        <Band tone="void">
          <Wrap>
            <SectionHead align="left" accent={A} eyebrow="How Shop Credits work" title="One simple currency for the Virtual Shop" />
            <Body style={{ marginBottom: 20 }}>
              Every product has a Shop Credit price. You buy the Shop Credits you want in a pack, and they stay with your VISINVOKE account until you use them - there is no subscription, and credits do not disappear at the end of a month. When you acquire a product, its Shop Credit price is deducted from your balance.
            </Body>
            <Callout
              accent={A}
              eyebrow="How purchasing Shop Credits works"
              title="Choose a pack, check out securely, get your credits"
              text="Pick one of the six Shop Credit packs on the Virtual Shop page and check out through the standard VISINVOKE secure payment flow. The price and the number of Shop Credits are set by VISINVOKE - once your payment completes, the credits are added to your balance, ready to spend."
              items={['Six packs, from a small starter pack up to the best-value Signature pack', 'Prices are in US dollars (USD) and shown clearly on every pack', 'Credits are added to your account after checkout', 'Larger packs include progressively more value']}
            />
            <ShopShot file="credits-ui.png" title="Real staged Shop Credits view" caption="Pack choice, value and the account credit model remain visible before checkout." />
          </Wrap>
        </Band>

        {/* ------------------------------------------- ACQUIRING AN ITEM */}
        <Band tone="deep">
          <Wrap>
            <SectionHead align="center" accent={A} eyebrow="How acquiring an item works" title="From browsing to owning, in a few steps" />
            <FlowRail accent={A} steps={ACQUIRE_STEPS} />
            <ShopShot file="cart-ui.png" title="Real staged acquisition view" caption="The selected product and Shop Credit cost are reviewed before the customer confirms acquisition." />
          </Wrap>
        </Band>

        {/* ------------------------------------------------- OWNERSHIP */}
        <Band tone="void">
          <Wrap>
            <SectionHead align="left" accent={A} eyebrow="What ownership means" title="What you acquire stays yours" />
            <Body style={{ marginBottom: 20 }}>
              Acquired products are attached to your VISINVOKE account. Ownership is persistent: your collection is there every time you come back, and items you already own remain owned even as the catalogue changes over time - new products arrive and areas are updated, but that never removes what you have already collected.
            </Body>
            <Callout
              accent={A}
              eyebrow="Persistent ownership"
              title="Owned means owned"
              text="If a product’s placement in the catalogue changes later, the product you acquired stays in your collection exactly as you acquired it - including any configuration you chose when you added it."
            />
            <ShopShot file="ownership-ui.png" title="Real staged ownership state" caption="Owned products remain attached to the account with their acquired configuration." />
          </Wrap>
        </Band>

        {/* --------------------------------------- COLLECTION SURFACES */}
        <Band tone="panel">
          <Wrap>
            <SectionHead
              align="split"
              accent={A}
              eyebrow="Your collection"
              title="My Collection, Showcase and Recently Acquired"
              lead="As your collection grows, VISINVOKE gives you meaningful ways to organise, feature and revisit what you own."
            />
            <CardGrid accent={A} cols={3} variant="bar" items={COLLECTION_CARDS} />
            <ShopShot file="collection-ui.png" title="Real staged collection" caption="A populated collection brings owned items together for revisiting and display." />
          </Wrap>
        </Band>

        {/* -------------------------- DISPLAY VALUE + PRIVACY + BUILDING */}
        <Band tone="void">
          <Wrap>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <Callout
                accent={A}
                eyebrow="Display value vs Shop Credits"
                title="A guide value, and the credits you actually spend"
                text="Some products show a display value alongside their Shop Credit price - a for-interest guide to what the item represents. The amount you actually spend is always the Shop Credit price, paid from the credits in your balance."
              />
              <CardGrid
                accent={A}
                cols={2}
                variant="quiet"
                items={[
                  { sub: 'Privacy', title: 'Private by default', text: 'Your collection is private by default. What you own is yours to keep to yourself.' },
                  { sub: 'Keep building', title: 'Continue over time', text: 'Browse whenever you like and keep adding to your collection. There is no rush - build it up at your own pace, one acquisition at a time.' },
                ]}
              />
              <NoteStrip title="Good to know">
                Shop Credits are used only inside the Virtual Shop. Product credit prices range from everyday collectibles through to premium vehicles, property and aspirational items, so there is always something within reach and always something to build towards.
              </NoteStrip>
            </div>
          </Wrap>
        </Band>

        {/* ------------------------------------------------------- CTA */}
        <CtaBand
          accent={A}
          eyebrow="Ready to start?"
          title="Open the Virtual Shop and start your collection"
          text="See the six Shop Credit packs, choose the one that suits you, and begin exploring the catalogue."
          back={back}
        >
          <ActionButton accent={A} onClick={() => navigate('/dashboard/virtual-shop')}>Open Virtual Shop</ActionButton>
        </CtaBand>
      </div>
    </MarketingPage>
  );
}
